import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/store";

/**
 * A counter per client that goes up each time that client's flags change from this app.
 *
 * The chart shows a client's flags in more than one place — the info box and the Flag Archive tab —
 * and they sit in separate parts of the client profile. A surface that reads flags watches its
 * client's counter and reads again when it moves, so a removal in the drawer reaches all of them.
 */
interface ClientFlagsState {
  revisions: Record<string, number>;
}

const initialState: ClientFlagsState = { revisions: {} };

export const clientFlagsSlice = createSlice({
  name: "clientFlags",
  initialState,
  reducers: {
    clientFlagsChanged: (state, action: PayloadAction<string>) => {
      const clientId = action.payload;
      state.revisions[clientId] = (state.revisions[clientId] ?? 0) + 1;
    },
  },
});

export const { clientFlagsChanged } = clientFlagsSlice.actions;

export default clientFlagsSlice.reducer;

/**
 * @param state - The root state
 * @param clientId - Patient logical id
 * @returns How many times this client's flags have changed in this session; 0 when never.
 */
export const selectClientFlagsRevision = (state: RootState, clientId: string) =>
  state.clientFlags.revisions[clientId] ?? 0;
